import { AuthProvider, useAuth } from '@luminovo/auth';
import { id } from '@luminovo/commons';
import { analytics } from '@luminovo/data-stack';
import * as Sentry from '@sentry/react';
import React from 'react';

import { AuthBootstrapTelemetry } from '@/app/authBootstrapTelemetry';
import { TenantDetailsProvider, useTenantDetailsContext } from '@/shared/data/contexts/TenantDetailsContext';
import { SadBox } from '@/shared/ui/errors/SadBox';

import type { ReportedRouterAuth } from './routerAuth';

/** What `AuthProvider` replaces the URL through after Auth0's callback: only `replace`. */
type AuthRedirectHistory = React.ComponentProps<typeof AuthProvider>['history'];

/** Tenant, then Auth0 (needs its org), above the router; a bare Sentry boundary, as the router renders below. */
export function TenantAndAuthProviders({
    auth,
    history,
    children,
}: {
    auth: ReportedRouterAuth;
    history: AuthRedirectHistory;
    children: React.ReactNode;
}): JSX.Element {
    return (
        <Sentry.ErrorBoundary fallback={<SadBox />}>
            <TenantDetailsProvider>
                <TenantAuthProvider auth={auth} history={history}>
                    {children}
                </TenantAuthProvider>
            </TenantDetailsProvider>
        </Sentry.ErrorBoundary>
    );
}

function TenantAuthProvider({
    auth,
    history,
    children,
}: {
    auth: ReportedRouterAuth;
    history: AuthRedirectHistory;
    children: React.ReactNode;
}): JSX.Element {
    const { tenantDetails } = useTenantDetailsContext();
    const auth0OrgId = tenantDetails.external_auth_org_id ?? undefined;

    return (
        <AuthProvider
            onRedirectCallback={(_, user) => {
                if (user) {
                    analytics.track(id('auth/login_success'));
                } else {
                    analytics.track(id('auth/login_failed'));
                }
            }}
            auth0OrgId={auth0OrgId}
            history={history}
        >
            {/* Next to the provider, so it times the SDK getting ready, not the first route render. */}
            <AuthBootstrapTelemetry />
            <ReportAuthToRouter auth={auth} auth0OrgId={auth0OrgId} />
            {children}
        </AuthProvider>
    );
}

/** Hands the Auth0 SDK's state to the router's `context.auth` once loaded and on every change. */
function ReportAuthToRouter({ auth, auth0OrgId }: { auth: ReportedRouterAuth; auth0OrgId: string | undefined }): null {
    // With the tenant's organization: the router asks for a token with it, as `AuthenticatedLayout` does.
    const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth({ auth0OrgId });

    React.useEffect(() => {
        if (!isLoading) {
            auth.report({ isAuthenticated, getAccessTokenSilently });
        }
    }, [auth, isLoading, isAuthenticated, getAccessTokenSilently]);

    return null;
}
